import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createGodown,
  createInvitation,
  deleteDistrict,
} from "../lib/api";
import type { Godown, Invitation } from "../lib/api";
import {
  GODOWN_STATUS_META,
  GODOWN_STATUS_ORDER,
  StatusTag,
  godownStatusOf,
} from "../lib/godownStatus";
import {
  DASH,
  districtHeadUser,
  fmtInt,
  fullName,
  isPass,
  shortDate,
  useSHDistricts,
  useSHGodowns,
  useSHInvitations,
  useSHReports,
  useSHState,
  useSHUsers,
} from "./data";

/** The grain(s) a godown grades, as an uppercase chip label. */
function godownGrain(g: Godown): string | null {
  const set = new Set<string>();
  (g.grain_configs ?? []).forEach((c) => set.add(c.grain_type));
  if (!set.size && g.grain_type) set.add(g.grain_type);
  if (!set.size) return null;
  if (set.size === 1) return [...set][0].toUpperCase();
  return `${set.size} GRAINS`;
}

/** The district head we invited for this district — accepted first,
 * else a still-pending invite, else none. */
function districtHead(invites: Invitation[], districtId: number) {
  const mine = invites.filter(
    (i) =>
      i.role === "district_head" &&
      i.target_scope_type === "district" &&
      i.target_scope_id === districtId,
  );
  const accepted = mine.find((i) => i.accepted_at);
  const pending = mine.find((i) => !i.accepted_at && !i.revoked_at);
  return { head: accepted || pending || null, accepted: !!accepted };
}

export function DistrictDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const districtId = Number(id);

  const stateQ = useSHState();
  const districtsQ = useSHDistricts();
  const godownsQ = useSHGodowns();
  const reportsQ = useSHReports();
  const invitesQ = useSHInvitations();
  const usersQ = useSHUsers();

  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const say = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  };

  const state = stateQ.data?.state;
  const districts = districtsQ.data?.districts ?? [];
  const allGodowns = godownsQ.data?.godowns ?? [];
  const allReports = reportsQ.data?.reports ?? [];
  const invites = invitesQ.data?.invitations ?? [];
  const users = usersQ.data?.users ?? [];

  const district = districts.find((d) => d.id === districtId);
  const headUser = districtHeadUser(users, districtId);
  const headName = headUser ? fullName(headUser) : null;

  const model = useMemo(() => {
    const godowns = allGodowns.filter((g) => g.district_id === districtId);
    const gids = new Set(godowns.map((g) => g.id));
    const reports = allReports.filter(
      (r) => r.godown_id != null && gids.has(r.godown_id),
    );

    const perGodown = new Map<number, { filed: number; passed: number }>();
    for (const g of godowns) perGodown.set(g.id, { filed: 0, passed: 0 });
    for (const r of reports) {
      const slot = perGodown.get(r.godown_id as number);
      if (!slot) continue;
      slot.filed += 1;
      if (isPass(r.verdict)) slot.passed += 1;
    }

    const groups = new Map<string, Godown[]>();
    for (const g of godowns) {
      const key = (g.place ?? "").trim();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(g);
    }
    const places = [...groups.keys()].sort((a, b) =>
      a === "" ? 1 : b === "" ? -1 : a.localeCompare(b),
    );

    const grains = new Set<string>();
    for (const g of godowns) {
      (g.grain_configs ?? []).forEach((c) => grains.add(c.grain_type));
      if ((!g.grain_configs || !g.grain_configs.length) && g.grain_type)
        grains.add(g.grain_type);
    }

    const passed = reports.filter((r) => isPass(r.verdict)).length;
    // Count godowns by their real operational status (setup/filling/
    // filled), not the old reports-filed heuristic.
    const statusCounts = { setup: 0, filling: 0, filled: 0 };
    for (const g of godowns) statusCounts[godownStatusOf(g)] += 1;

    return {
      godowns,
      reports,
      perGodown,
      groups,
      places,
      grains: [...grains],
      passed,
      statusCounts,
      placeNames: places.filter(Boolean),
    };
  }, [allGodowns, allReports, districtId]);

  const { head } = districtHead(invites, districtId);
  const stateName = (state?.name ?? "STATE").toUpperCase();

  if (districtsQ.isLoading) {
    return (
      <>
        <p className="crumb">
          <Link className="crumb-link" to="/sh/districts">
            {stateName} / DISTRICTS
          </Link>{" "}
          / …
        </p>
        <div className="card">
          <div className="card-body">
            <div className="tray-cap">Loading…</div>
          </div>
        </div>
      </>
    );
  }
  if (!district) {
    return (
      <>
        <p className="crumb">
          <Link className="crumb-link" to="/sh/districts">
            {stateName} / DISTRICTS
          </Link>{" "}
          / NOT FOUND
        </p>
        <div className="card">
          <div className="card-body">
            <div className="empty" style={{ border: 0, padding: "34px 20px" }}>
              <b>That district isn't in your state</b>
              <p>It may have moved or you may not have access to it.</p>
              <Link className="btn" to="/sh/districts">
                Back to districts
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const totalFiled = model.reports.length;
  const passRate = totalFiled
    ? Math.round((model.passed / totalFiled) * 100)
    : null;

  return (
    <>
      <p className="crumb">
        <Link className="crumb-link" to="/sh/districts">
          {stateName} / DISTRICTS
        </Link>{" "}
        / {district.name.toUpperCase()}
      </p>
      <div className="page-head">
        <div>
          <h1>{district.name} district</h1>
          <p>
            <span className="mono">{district.code || `#${district.id}`}</span>
            {district.zone ? ` · ${district.zone.toLowerCase()} zone` : ""} ·
            in-charge {headName ?? (head ? head.email : DASH)}
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-gold" onClick={() => setAddOpen(true)}>
            Add godown
          </button>
          <Link className="btn" to="/sh/reports">
            View reports
          </Link>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <span>PLACES</span>
          <b>{fmtInt(model.placeNames.length)}</b>
          <i>
            {model.placeNames.length
              ? model.placeNames.slice(0, 2).join(", ") +
                (model.placeNames.length > 2
                  ? ` +${model.placeNames.length - 2}`
                  : "")
              : "none set"}
          </i>
        </div>
        <div className="stat">
          <span>GODOWNS</span>
          <b>{fmtInt(model.godowns.length)}</b>
          <i>
            {GODOWN_STATUS_ORDER.filter((s) => model.statusCounts[s] > 0)
              .map((s) => `${model.statusCounts[s]} ${GODOWN_STATUS_META[s].short}`)
              .join(" · ") || "none yet"}
          </i>
        </div>
        <div className="stat">
          <span>REPORTS</span>
          <b>{fmtInt(totalFiled)}</b>
          <i>filed by field devices</i>
        </div>
        <div className="stat">
          <span>REPORTS PASSED</span>
          {passRate != null ? (
            <>
              <b>
                {passRate}
                <small style={{ fontSize: 14 }}>%</small>
              </b>
              <i className="up">{fmtInt(model.passed)} passed</i>
            </>
          ) : (
            <>
              <b>{DASH}</b>
              <i>no reports yet</i>
            </>
          )}
        </div>
        <div className="stat">
          <span>GRAINS</span>
          <b>{model.grains.length ? fmtInt(model.grains.length) : DASH}</b>
          <i>
            {model.grains.length
              ? model.grains.map((g) => g.toLowerCase()).join(", ")
              : "not configured"}
          </i>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="card-head">
          <div>
            <h2>Godowns by place</h2>
            <p>
              Grouped by place. Open a godown to manage its agents, grain
              parameters and reports.
            </p>
          </div>
          <div className="right">
            <button className="btn" onClick={() => setAddOpen(true)}>
              Add godown
            </button>
          </div>
        </div>
        {model.godowns.length === 0 ? (
          <div style={{ padding: 22 }}>
            <div className="empty" style={{ border: 0 }}>
              <b>No godowns yet</b>
              <p>
                Add a godown to this district — or the district head can add
                their own once they confirm.
              </p>
              <button className="btn btn-gold" onClick={() => setAddOpen(true)}>
                Add godown
              </button>
            </div>
          </div>
        ) : (
          <div className="tscroll">
            <table className="stock" style={{ border: 0, borderRadius: 0 }}>
              <thead>
                <tr>
                  <th>GODOWN</th>
                  <th>GRAIN</th>
                  <th className="r">REPORTS FILED</th>
                  <th className="r">PASSED</th>
                  <th>STATUS</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {model.places.map((place) => {
                  const gs = model.groups.get(place) ?? [];
                  const label = place ? place.toUpperCase() : "PLACE NOT SET";
                  return (
                    <GroupRows
                      key={place || "__none"}
                      label={label}
                      godowns={gs}
                      perGodown={model.perGodown}
                      onOpen={(gid) => navigate(`/sh/godown/${gid}`)}
                    />
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>DISTRICT TOTAL</td>
                  <td className="r">{fmtInt(totalFiled)}</td>
                  <td className="r">{fmtInt(model.passed)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>District in-charge</h2>
          </div>
        </div>
        <dl className="dl">
          <div>
            <dt>NAME</dt>
            <dd>{headName ?? DASH}</dd>
          </div>
          <div>
            <dt>WORK EMAIL</dt>
            <dd>{headUser?.email ?? head?.email ?? DASH}</dd>
          </div>
          <div>
            <dt>ROLE</dt>
            <dd>District head</dd>
          </div>
          <div>
            <dt>PROFILE CONFIRMED</dt>
            <dd>{head?.accepted_at ? shortDate(head.accepted_at) : DASH}</dd>
          </div>
        </dl>
      </div>

      <DeleteDistrictCard
        districtId={district.id}
        districtName={district.name}
        godownCount={model.godowns.length}
        onDeleted={() => navigate("/sh/districts")}
      />

      <AddGodownDrawer
        open={addOpen}
        districtId={district.id}
        onClose={() => setAddOpen(false)}
        onDone={(msg) => {
          setAddOpen(false);
          say(msg);
        }}
      />

      <div className={"toast" + (toast ? " show" : "")}>
        <span className="dot" />
        <span>{toast}</span>
      </div>
    </>
  );
}

function GroupRows({
  label,
  godowns,
  perGodown,
  onOpen,
}: {
  label: string;
  godowns: Godown[];
  perGodown: Map<number, { filed: number; passed: number }>;
  onOpen: (godownId: number) => void;
}) {
  return (
    <>
      <tr className="grp">
        <td colSpan={6}>
          {label} · {godowns.length} GODOWN{godowns.length === 1 ? "" : "S"}
        </td>
      </tr>
      {godowns.map((g) => {
        const c = perGodown.get(g.id) ?? { filed: 0, passed: 0 };
        const live = c.filed > 0;
        const grain = godownGrain(g);
        return (
          <tr key={g.id} className="clickable" onClick={() => onOpen(g.id)}>
            <td>
              <b>{g.name}</b>
            </td>
            <td>
              {grain ? (
                <span className="mono">{grain}</span>
              ) : (
                <span className="mono" style={{ color: "var(--muted)" }}>
                  {DASH}
                </span>
              )}
            </td>
            <td className="r mono">{live ? fmtInt(c.filed) : DASH}</td>
            <td className="r mono">{live ? fmtInt(c.passed) : DASH}</td>
            <td>
              <StatusTag status={godownStatusOf(g)} />
            </td>
            <td className="r">
              <svg className="chev" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 3.5L10.5 8 6 12.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ------------------------------------------------------------------
// Add godown to this district (state head)
// ------------------------------------------------------------------

function AddGodownDrawer({
  open,
  districtId,
  onClose,
  onDone,
}: {
  open: boolean;
  districtId: number;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [email, setEmail] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const gres = await createGodown({
        district_id: districtId,
        name: name.trim(),
        place: place.trim() || undefined,
      });
      if (email.trim()) {
        await createInvitation({
          email: email.trim(),
          role: "godown_qc_agent",
          target_scope_type: "godown",
          target_scope_id: gres.godown.id,
        });
      }
      return name.trim();
    },
    onSuccess: (savedName) => {
      qc.invalidateQueries({ queryKey: ["sh-godowns"] });
      qc.invalidateQueries({ queryKey: ["sh-invitations"] });
      setName("");
      setPlace("");
      setEmail("");
      onDone(
        email.trim()
          ? `${savedName} added. Invite sent to the QC agent.`
          : `${savedName} added.`,
      );
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <>
      <div
        className={"scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={"drawer" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-head">
          <div>
            <h2>Add a godown</h2>
            <p>
              Add a godown to this district. You can invite its QC agent now or
              leave it for later.
            </p>
          </div>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit} style={{ display: "contents" }}>
          <div className="drawer-body">
            <span className="step">GODOWN</span>
            <div className="grid-2">
              <div className="field">
                <label>Godown name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sonbarsa Godown"
                  autoFocus
                />
              </div>
              <div className="field">
                <label>
                  Place<span className="hint">optional</span>
                </label>
                <input
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                  placeholder="Sonbarsa"
                />
              </div>
            </div>
            <span className="step">QC AGENT</span>
            <div className="field">
              <label>
                Work email
                <span className="hint">optional — the invite goes here</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            {mut.isError && (
              <div
                className="notice"
                style={{ background: "#FDE8E8", borderColor: "#F5B5B5" }}
              >
                <p style={{ color: "#B00020" }}>{(mut.error as Error).message}</p>
              </div>
            )}
          </div>
          <div className="drawer-foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-gold" disabled={mut.isPending}>
              {mut.isPending ? "Saving…" : "Add godown"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

// ------------------------------------------------------------------
// Delete district (danger zone)
// ------------------------------------------------------------------

function DeleteDistrictCard({
  districtId,
  districtName,
  godownCount,
  onDeleted,
}: {
  districtId: number;
  districtName: string;
  godownCount: number;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const mut = useMutation({
    mutationFn: () => deleteDistrict(districtId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sh-districts"] });
      onDeleted();
    },
  });

  return (
    <div
      className="card"
      style={{ borderColor: "#F0C9C9", background: "#FDF7F7" }}
    >
      <div className="card-head">
        <div>
          <h2>Danger zone</h2>
          <p>
            {godownCount > 0
              ? `Delete every godown in ${districtName} first — a district with godowns can't be removed.`
              : `Deleting ${districtName} removes the district. This can't be undone.`}
          </p>
        </div>
        <div className="right">
          {confirm ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="btn btn-danger"
                disabled={mut.isPending}
                onClick={() => mut.mutate()}
              >
                {mut.isPending ? "Deleting…" : "Yes, delete district"}
              </button>
              <button className="btn" onClick={() => setConfirm(false)}>
                Keep
              </button>
            </div>
          ) : (
            <button className="btn" onClick={() => setConfirm(true)}>
              Delete district
            </button>
          )}
        </div>
      </div>
      {mut.isError && (
        <div className="card-body">
          <span className="tray-cap" style={{ color: "#B00020", margin: 0 }}>
            {(mut.error as Error).message}
          </span>
        </div>
      )}
    </div>
  );
}
